/**
 * What the dock has to know before it can ask for the LAYER-STACK COMPLIANCE
 * REPORT (spec §7) — the document with the structure summary, the compliance
 * table and the roll-up verdict.
 *
 * Two numbers and a window, and each one has a reason:
 *
 * - the **window** is the brushed selection, expressed in INTERNAL stations,
 *   because the server applies one window to EVERY layer
 *   (`preprocess.ts` :: the `LAYER_STACK` arm's `layerBins`);
 * - `layerCount` is N−1, printed in the dialog's scope line so the reader can
 *   see what the page budget is being spent on;
 * - `binCount` is the CROSS-LAYER total inside that window — every layer's
 *   bins summed, not the active layer's — because ruling R16 spends ONE
 *   400-page budget across the whole stack, so a per-layer number would
 *   under-quote a deep stack by a factor of N−1 and make a rejection look
 *   arbitrary.
 *
 * `binsInWindow` here is the client twin of `preprocess.ts`'s function of the
 * same name: same half-open-free inclusive test, same 1e-6 slack. It has to
 * be, or the count in the dialog is not the count the server prices.
 */
import type { StackLayer, StackResult, StationBin } from '@/lib/stationVolumes/types';

export interface LayerStackReportScope {
  /** Internal stations, or `null` for "the whole run". */
  stationFrom: number | null;
  stationTo: number | null;
  /** The same window in DISPLAY stations, for the dialog's scope line. */
  displayFrom: number | null;
  displayTo: number | null;
  /** N−1. Every layer, PENDING ones included — they get a section too. */
  layerCount: number;
  /** Every layer's bins inside the window, summed. */
  binCount: number;
}

/** Client twin of `preprocess.ts` :: `binsInWindow`. */
export const stackBinsInWindow = (
  bins: readonly StationBin[],
  stationFrom: number | null,
  stationTo: number | null,
): StationBin[] => {
  const lo = typeof stationFrom === 'number' ? stationFrom : -Infinity;
  const hi = typeof stationTo === 'number' ? stationTo : Infinity;
  return bins.filter((b) => b.staStartInternalM >= lo - 1e-6 && b.staEndInternalM <= hi + 1e-6);
};

/**
 * @param doc      the fetched `stack.json` — the only thing that holds every
 *                 layer's bins. `null` while it is loading, and the caller
 *                 keeps the menu item inert until it lands rather than
 *                 quoting a zero.
 * @param activeBins the bins the chart is brushing, i.e. the ACTIVE layer's.
 *                 The window is read off these because that is the axis the
 *                 user dragged on; it is then applied to every layer.
 * @param selection the brush, `null` for the whole corridor.
 */
export const layerStackReportScope = (
  doc: StackResult | null,
  activeBins: readonly StationBin[],
  selection: { fromBin: number; toBin: number } | null,
): LayerStackReportScope => {
  const layers: StackLayer[] = doc?.layers ?? [];
  const lo = selection ? Math.max(0, Math.min(selection.fromBin, selection.toBin)) : 0;
  const hi = selection
    ? Math.min(activeBins.length - 1, Math.max(selection.fromBin, selection.toBin))
    : activeBins.length - 1;
  /**
   * A brush left behind by a longer run can sit entirely past the end of this
   * layer's bins. `perStationPdfScope` widens to the whole corridor in that
   * case and so does this: a range of "nothing" is not a range, and the two
   * items in the same menu must not disagree about what the brush means.
   */
  const windowed = selection !== null && activeBins.length > 0 && lo <= hi;
  const first = windowed ? activeBins[lo] : undefined;
  const last = windowed ? activeBins[hi] : undefined;
  const stationFrom = first ? first.staStartInternalM : null;
  const stationTo = last ? last.staEndInternalM : null;
  return {
    stationFrom,
    stationTo,
    displayFrom: first ? first.staStartDisplayM : null,
    displayTo: last ? last.staEndDisplayM : null,
    layerCount: layers.length,
    // A PENDING layer has no `stations` key at all — it contributes no bins,
    // which is not the same as contributing zero measured ones.
    binCount: layers.reduce((n, l) => n + stackBinsInWindow(l.stations ?? [], stationFrom, stationTo).length, 0),
  };
};
